'use client';
import { useMemo, useState } from 'react';

import { GovernmentTable, type GovernmentTradeRow } from '../../../../src/components/GovTrades';
import { TickerTradesTable } from '../securities/_components/TickerTradesTable';
import { GovTradesTabsBar, type GovTradesTabItem } from './GovTradesTabsBar';
import type { TabData } from './constants';

/** A ticker-scoped trades table; the full history is passed in and filtered client-side. */
export interface ProfileTradesTab {
  variant: 'trades';
  key: string;
  name: string;
  ticker: string;
  rows: GovernmentTradeRow[];
}

/** A curated aggregation table (trades or traders) backed by the pre-bucketed stats arrays. */
export interface ProfileTradersTab extends TabData {
  variant: 'traders';
}

/** A tab that navigates elsewhere (e.g. "Stock Report") instead of switching the table. */
export interface ProfileLinkTab {
  variant: 'link';
  key: string;
  name: string;
  href: string;
  external?: boolean;
}

export type ProfileTab = ProfileTradesTab | ProfileTradersTab | ProfileLinkTab;

/**
 * Tabbed tables for gov-trades profile pages. Trade tabs reuse the securities
 * `AllTradesTable` (toolbar + live filters, ticker-scoped); trader/aggregation
 * tabs use `GovernmentTable`; link tabs navigate. Reusable across the security
 * and member profile pages.
 */
export const ProfileTabbedTables = ({ tabs }: { tabs: ProfileTab[] }) => {
  const firstSelectable = tabs.find(tab => tab.variant !== 'link');
  const [activeKey, setActiveKey] = useState(firstSelectable?.key ?? '');

  const activeTab = useMemo(() => tabs.find(tab => tab.key === activeKey), [activeKey, tabs]);

  const barTabs: GovTradesTabItem[] = tabs.map(tab =>
    tab.variant === 'link'
      ? { key: tab.key, name: tab.name, href: tab.href, external: tab.external }
      : { key: tab.key, name: tab.name },
  );

  if (!tabs.length) return null;

  return (
    <div className="w-full gap-4">
      <GovTradesTabsBar activeKey={activeKey} onSelect={setActiveKey} tabs={barTabs} />
      <div className="tab-content">
        <div className="table-wrapper">
          {activeTab?.variant === 'trades' && <TickerTradesTable rows={activeTab.rows} ticker={activeTab.ticker} />}
          {activeTab?.variant === 'traders' && (
            <GovernmentTable
              columnsDef={activeTab.columnsDef}
              description={activeTab.description ?? ''}
              height={789}
              hiddenColumns={activeTab.hideColumns}
              hideMostRows={true}
              layout="all-trades"
              rowData={activeTab.data}
              title={activeTab.title}
              variant="light"
            />
          )}
        </div>
      </div>
    </div>
  );
};
